import { LEGACY_THEME_TYPES } from 'src/components/styled/theme/ThemeProvider';

export const USER_PREFERENCES_VERSION = 1;
export const USER_PREFERENCES_KEY_PREFIX = 'lyt:userPrefs:';
export const USER_PREFERENCES_LAST_USER_KEY = 'lyt:userPrefs:__lastUser';

const VALID_THEMES = new Set([
    LEGACY_THEME_TYPES.light,
    LEGACY_THEME_TYPES.dark,
]);

const normalizePreferenceId = (id) => String(id ?? '').trim().toLowerCase();

/**
 * Normalize an email address for use as a localStorage key segment.
 * @param {string|undefined|null} email
 * @returns {string|null}
 */
export const normalizeUserPreferencesKey = (email) => {
    if (!email || typeof email !== 'string') {
        return null;
    }

    const normalized = email.trim().toLowerCase();
    return normalized || null;
};

/**
 * @param {string|undefined|null} email
 * @returns {string|null}
 */
export const getUserPreferencesStorageKey = (email) => {
    const normalized = normalizeUserPreferencesKey(email);
    if (!normalized) {
        return null;
    }

    return `${USER_PREFERENCES_KEY_PREFIX}${normalized}`;
};

/**
 * @param {string|undefined|null} theme
 * @returns {boolean}
 */
export const isValidUserTheme = (theme) => VALID_THEMES.has(theme);

/**
 * @param {string|undefined|null} email
 * @returns {import('./user-preferences.types').UserPreferences|null}
 */
export const readUserPreferences = (email) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    const storageKey = getUserPreferencesStorageKey(email);
    if (!storageKey) {
        return null;
    }

    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== USER_PREFERENCES_VERSION) {
            return null;
        }

        return {
            version: USER_PREFERENCES_VERSION,
            theme: isValidUserTheme(parsed.theme) ? parsed.theme : undefined,
            agencyId: typeof parsed.agencyId === 'string' ? parsed.agencyId : '',
            cityId: typeof parsed.cityId === 'string' ? parsed.cityId : '',
            jurisdictionId: typeof parsed.jurisdictionId === 'string'
                ? parsed.jurisdictionId
                : '',
        };
    } catch {
        return null;
    }
};

/**
 * Merge and persist UI preferences for the given user.
 * @param {string|undefined|null} email
 * @param {Partial<import('./user-preferences.types').UserPreferences>} partialPrefs
 */
export const writeUserPreferences = (email, partialPrefs = {}) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    const normalized = normalizeUserPreferencesKey(email);
    const storageKey = getUserPreferencesStorageKey(email);
    if (!normalized || !storageKey) {
        return;
    }

    const existing = readUserPreferences(email) || {
        version: USER_PREFERENCES_VERSION,
        agencyId: '',
        cityId: '',
        jurisdictionId: '',
    };

    const preferStoredValue = (nextValue, previousValue) => {
        if (nextValue !== undefined && nextValue !== null && nextValue !== '') {
            return nextValue;
        }

        return previousValue ?? '';
    };

    const nextPrefs = {
        version: USER_PREFERENCES_VERSION,
        agencyId: preferStoredValue(partialPrefs.agencyId, existing.agencyId),
        cityId: preferStoredValue(partialPrefs.cityId, existing.cityId),
        jurisdictionId: preferStoredValue(
            partialPrefs.jurisdictionId,
            existing.jurisdictionId,
        ),
    };

    if (partialPrefs.theme !== undefined) {
        if (isValidUserTheme(partialPrefs.theme)) {
            nextPrefs.theme = partialPrefs.theme;
        }
    } else if (existing.theme) {
        nextPrefs.theme = existing.theme;
    }

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(nextPrefs));
        window.localStorage.setItem(USER_PREFERENCES_LAST_USER_KEY, normalized);
    } catch {
        // localStorage can throw in private mode or when quota is exceeded.
    }
};

/**
 * Apply the last known user's theme before React paints to avoid a flash.
 */
export const applyBootstrapThemeFromStorage = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    try {
        const lastUser = window.localStorage.getItem(USER_PREFERENCES_LAST_USER_KEY);
        if (!lastUser) {
            return;
        }

        const prefs = readUserPreferences(lastUser);
        if (!prefs?.theme) {
            return;
        }

        document.documentElement.setAttribute('data-theme', prefs.theme);
    } catch {
        // Non-fatal — Redux will apply the saved theme after auth bootstrap.
    }
};

/**
 * Find a jurisdiction across the user's agency list.
 * @param {Array} agencies
 * @param {string} jurisdictionId
 * @returns {{ agencyId: string, jurisdictionId: string }|null}
 */
export const findJurisdictionInAgencies = (agencies, jurisdictionId) => {
    const normalizedJurisdictionId = normalizePreferenceId(jurisdictionId);
    if (!normalizedJurisdictionId || !Array.isArray(agencies)) {
        return null;
    }

    for (const agency of agencies) {
        const match = (agency.jurisdictions || []).find(
            (jurisdiction) => normalizePreferenceId(jurisdiction.id)
                === normalizedJurisdictionId,
        );

        if (match) {
            return {
                agencyId: agency.agencies_id,
                jurisdictionId: match.id,
            };
        }
    }

    return null;
};

const agencyMatchesPreferenceId = (left, right) => (
    normalizePreferenceId(left?.agencies_id ?? left?.id)
        === normalizePreferenceId(right)
);

export { agencyMatchesPreferenceId, normalizePreferenceId };

/**
 * True when a stored jurisdiction belongs to the stored agency (or either is absent).
 * @param {Array} agencies
 * @param {import('./user-preferences.types').UserPreferences|null|undefined} storedPreferences
 * @returns {boolean}
 */
export const storedLocationPreferencesAreConsistent = (agencies, storedPreferences) => {
    const agencyId = storedPreferences?.agencyId;
    const jurisdictionId = storedPreferences?.jurisdictionId;

    if (!agencyId || !jurisdictionId) {
        return true;
    }

    const parent = findJurisdictionInAgencies(agencies, jurisdictionId);
    if (!parent) {
        return true;
    }

    return agencyMatchesPreferenceId({ agencies_id: parent.agencyId }, agencyId);
};

/**
 * Derive city/jurisdiction ids to persist, keeping them aligned with the active agency.
 * @param {object} state
 * @returns {{ cityId: string, jurisdictionId: string }}
 */
export const resolvePersistedLocationPreferenceIds = (state) => {
    const agencyId = state.user?.selectedLocation?.agencyId || state.user?.agencyId || '';
    const cityIdFromLocation = state.user?.selectedLocation?.cityId || '';
    const selectedJurisdiction = state.userInfo?.selectedJurisdiction || '';
    const agencies = state.userInfo?.agencies?.list || [];

    if (selectedJurisdiction && agencyId) {
        const parent = findJurisdictionInAgencies(agencies, selectedJurisdiction);
        if (parent && agencyMatchesPreferenceId({ agencies_id: parent.agencyId }, agencyId)) {
            return {
                cityId: selectedJurisdiction,
                jurisdictionId: selectedJurisdiction,
            };
        }
    }

    if (selectedJurisdiction && !agencyId) {
        return {
            cityId: selectedJurisdiction,
            jurisdictionId: selectedJurisdiction,
        };
    }

    return {
        cityId: cityIdFromLocation,
        jurisdictionId: cityIdFromLocation,
    };
};

/**
 * Resolve the email used to scope preference reads/writes.
 * Falls back to the last active user so bootstrap can run before profile hydration.
 * @param {object} [state]
 * @returns {string|null}
 */
export const resolvePreferencesEmail = (state) => {
    const email = state?.user?.personalInformation?.email;
    if (email) {
        return email;
    }

    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    return window.localStorage.getItem(USER_PREFERENCES_LAST_USER_KEY);
};

/**
 * @param {object} [state]
 * @returns {import('./user-preferences.types').UserPreferences|null}
 */
export const readStoredUserPreferencesForState = (state) => (
    readUserPreferences(resolvePreferencesEmail(state))
);

/**
 * @param {object} state
 */
export const persistUserPreferencesFromState = (state) => {
    const email = resolvePreferencesEmail(state);
    if (!email) {
        return;
    }

    const agencyId = state.user?.selectedLocation?.agencyId || state.user?.agencyId || '';
    const { cityId, jurisdictionId } = resolvePersistedLocationPreferenceIds(state);

    const partialPrefs = {
        theme: state.user?.theme,
    };

    if (agencyId) {
        partialPrefs.agencyId = agencyId;
    }

    if (cityId) {
        partialPrefs.cityId = cityId;
    }

    if (jurisdictionId) {
        partialPrefs.jurisdictionId = jurisdictionId;
    }

    writeUserPreferences(email, partialPrefs);
};
